//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

import { ColumnModel } from '../../api/api.models'
import { LocalDictionaryFactory } from '../../api/api.module'

import { GridService } from '../../../services/grid'

import { ConfigHelper, TemplateHelper, DataTypeGroupComponent, EventsNames, OperatorLayoutSettings, IUserSession, LocalizationHelper } from '../../../helpers/'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { GridBridge } from '../../common/common.interfaces'
import { GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { LoggerService } from '../../common/common.module'

import { Activity, Enums } from '../../../models/common/'
import { OpActor, OpActorGroup, OpDataTypeInGroup, OpLocation } from '../../../models/core/'
import { SESSION } from '../../../models/custom/session'

import { ActorHelper } from '../actors.helper'


interface ActorDetailsScope extends DetailsScope {
    ACTOR_COMMON_TAB_NAME: string
    ACTOR_GROUPS_ACCORDION_NAME: string
    ACTOR_LOCATIONS_ACCORDION_NAME: string
    ACTOR_PARAMS_ACCORDION_NAME: string

    id: number

    detailsParametersOptions: DetailsParametersOptions
    actorLocations: any[]
    actorGroups: any[]

    actorLocationsGridOptions: GridOptions
    actorLocationsGridEditOptions: GridBridge

    actorGroupsGridOptions: GridOptions
    actorGroupsGridEditOptions: GridBridge

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class ActorDetailsDirectiveController extends Details<ActorDetailsScope> {

    // #region Consts

    private get ACTOR_COMMON_TAB_NAME() { return 'ACTOR_COMMON_TAB_NAME' }
    private get ACTOR_GROUPS_ACCORDION_NAME() { return 'ACTOR_GROUPS_ACCORDION_NAME' }
    private get ACTOR_LOCATIONS_ACCORDION_NAME() { return 'ACTOR_LOCATIONS_ACCORDION_NAME' }
    private get ACTOR_PARAMS_ACCORDION_NAME() { return 'ACTOR_PARAMS_ACCORDION_NAME' }

    // #endregion


    // #region Members

    private referenceObject: OpActor
    private referenceObjectId: number

    // #endregion


    constructor(
        $scope: ActorDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        private gridService: GridService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.ContactPopupTabActive, userSession, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }


    // #region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpActor = null): void {
        this.$scope.options.isDataLoaded = false

        const deferred: JQueryDeferred<OpActor> = $.Deferred<OpActor>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider().Actor
                .getActor(this.referenceObjectId)
                .then((result: OpActor) => {
                    this.$scope.popupTitle = result.toString()

                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    let errorMsg: string = error

                    if (SESSION.isType(error)) {
                        errorMsg = (<SESSION>error).toString()
                    }

                    this.logger.log(errorMsg)
                    deferred.resolve(null)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpActor) => {
                if (result && (!this.referenceObject || this.referenceObject.idActor == result.idActor)) {
                    this.referenceObject = result
                    return $.when(
                        this.setAccordionEntrieOptions()
                    )
                }
            })
            .always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onEditActorGroups(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.$scope.detailsPopupId + 'group')
    }

    private onEditActorLocations(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.$scope.detailsPopupId + 'location')
    }

    private onEditActorParams(): void {
        const createEditInitParams: CreateEditInitParams = {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: this.referenceType,
            moduleName: this.moduleName,
            referenceObject: this.referenceObject,
            referenceObjectId: this.referenceObject.idActor,
            sections: null,
            useBusinessObjects: true
        }

        this.$scope.$root.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.referenceObject = null
        this.$scope.detailsParametersOptions = null
        this.$scope.actorGroups = null
        this.$scope.actorLocations = null

        this.$scope.actorLocationsGridOptions = null
        this.$scope.actorLocationsGridEditOptions = null
        this.$scope.actorGroupsGridOptions = null
        this.$scope.actorGroupsGridEditOptions = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        ActorHelper
            .deleteActor(this.userSession.getDataProvider(), this.referenceObjectId, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onSaveActorGroup(added: any[], removed: OpActorGroup[]): ng.IPromise<boolean> {
        const deferred = this.$q.defer<boolean>()

        if (_.size(added) == 0 && _.size(removed) == 0) {
            deferred.resolve(false)
            this.onRefresh()
        } else {
            ActorHelper
                .saveActorGroups(this.userSession.getDataProvider(), this.moduleName, this.referenceObject, added, removed)
                .then((savedObject: OpActor) => {
                    this.onRefresh(savedObject)

                    deferred.resolve(true)
                })
                .fail((error: any) => {
                    this.logger.log(error)

                    deferred.resolve(false)
                })
        }

        return deferred.promise
    }

    private onSaveActorLocation(added: any[], removed: any[]): ng.IPromise<boolean> {
        const deferred = this.$q.defer<boolean>()

        ActorHelper
            .saveActorLocations(this.userSession.getDataProvider(), this.moduleName, this.referenceObject, added, removed)
            .then((savedObject: OpActor) => {
                this.onRefresh(savedObject)

                deferred.resolve(true)
            })
            .fail((error: any) => {
                this.logger.log(error)

                deferred.resolve(false)
            })

        return deferred.promise;
    }

    // #endregion


    // #region Setup

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActorParameters)) {
            items.push({
                name: this.ACTOR_PARAMS_ACCORDION_NAME,
                showInTab: this.ACTOR_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ActorParameters),

                editMethod: () => this.onEditActorParams(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ContactDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActorGroups)) {
            items = []

            items.push({
                name: this.ACTOR_GROUPS_ACCORDION_NAME,
                showInTab: this.ACTOR_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Groups),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ActorGroups),

                editMethod: () => this.onEditActorGroups(),
                status: () => LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount) + (this.$scope.actorGroups ? this.$scope.actorGroups.length : 0),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ContactDetailsAccordions, 1, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActorLocations)) {
            items = []

            items.push({
                name: this.ACTOR_LOCATIONS_ACCORDION_NAME,
                showInTab: this.ACTOR_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Locations),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ActorLocations),

                editMethod: () => this.onEditActorLocations(),
                status: () => LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount) + (this.$scope.actorLocations ? this.$scope.actorLocations.length : 0),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ContactDetailsAccordions, 2, items))
        }

        return accordions
    }

    public getTabs(): any[] {
        return [{
            template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),

            name: this.ACTOR_COMMON_TAB_NAME
        }]
    }

    private setAccordionEntrieOptions(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        this.$scope.actorGroups = this.referenceObject.currentGroups
        this.$scope.actorLocations = this.referenceObject.currentLocations

        this.setActorGroupsGridEdit(this.referenceObject.currentGroups)
        this.setActorGroupsGridOptions(this.referenceObject.currentGroups)

        this.setBasicParameters()
            .always(() => {
                this.userSession
                    .getDataProvider().System
                    .getRequestColumnInfo(
                        Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__LOCATION_GRID_PARAMS,
                        this.moduleName,
                        { viewReferenceType: Enums.ReferenceType.IdLocation }
                    )
                    .then((columns: ColumnModel[]) => {
                        this.setActorLocationsGridEditOptions(this.referenceObject.currentLocations, columns)
                        this.setActorLocationsGridOptions(this.referenceObject.currentLocations, columns)
                    })
                    .always(() => {
                        def.resolve()
                    })
            })

        return def.promise()
    }

    private setBasicParameters(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()
        this.$scope.detailsParametersOptions = null

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.resolve()
            })

        if (this.isAccordionHidden(this.$scope.DEVICE_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    private setActorGroupsGridEdit(groups: OpActorGroup[]): void {
        this.$scope.actorGroupsGridEditOptions = {
            id: this.referenceObjectId,

            columns: ActorHelper.getActorGroupsGridColumns(),
            items: this.prepareItemsForActorGroupsEditGrid(groups),
            name: 'actorGroupsGrid',
            popupId: this.$scope.detailsPopupId + 'group',
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__GROUPS_EDIT, this.moduleName),
            allowDublicatedRow: false,
            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActorGroups),

            data: this.getActorGroupsGridEditData(),
            onSubmit: (added: any[], removed: any[]) => {
                return this.onSaveActorGroup(added, ActorHelper.handleSystemOwnerAddressBook(removed))
            }
        }
    }

    private getActorGroupsGridEditData(): JQueryPromise<any> {
        return this.userSession
            .getDataProvider().Actor
            .getActorGroupFilter([], 0, 0, '')
            .then((result: { objects: OpActorGroup[], totalCount: number, error: { code: any, msg: any } }) => {
                return result.objects.map((actorGroup: OpActorGroup) => {
                    return ActorHelper.extendToGridObject(actorGroup, this.gridService)
                })
            })
    }

    private prepareItemsForActorGroupsEditGrid(groups: OpActorGroup[]): OpActorGroup[] {
        if (_.size(groups)) {
            return groups.map((group: OpActorGroup) => {
                return ActorHelper.extendToGridObject(group.clone(true, false, false, false), this.gridService)
            })
        }
        return []
    }

    private setActorGroupsGridOptions(groups: OpActorGroup[]): void {
        this.$scope.actorGroupsGridOptions = null

        this.$timeout(() => {
            this.$scope.actorGroupsGridOptions = {
                addCustomFilters: true,
                columnsDictionary: ActorHelper.getActorGroupsGridColumns(),
                dataSource: groups,
                name: 'actorGroups',
                idReferenceType: Enums.ReferenceType.IdActor,
                type: GridTypes.PAGINATED_SMALL,

                allowColumnReordering: !this.userSession
                    .getLoggedOperator()
                    .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActorGroups),

                buttonsOptions: {
                    enabled: true,
                },
            }
        }, 100)
    }

    private setActorLocationsGridEditOptions(locations: OpLocation[], columns: ColumnModel[]): void {
        this.$scope.actorLocationsGridEditOptions = {
            columns,
            id: this.referenceObjectId,
            items: locations.map((location: OpLocation) => this.gridService.gridObjectFromIOpObject(location)),
            name: 'actorLocationsGrid',
            popupId: this.$scope.detailsPopupId + 'location',
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__LOCATIONS_EDIT, this.moduleName),

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActorLocations),

            getAvailableDataSourceMethod: this.userSession.
                getDataProvider().Location.getLocationFilter,

            onSubmit: (added, removed) => this.onSaveActorLocation(added, removed)
        }
    }

    private setActorLocationsGridOptions(locations: OpLocation[], columns: ColumnModel[]): void {
        this.$scope.actorLocationsGridOptions = null

        this.$timeout(() => {
            this.$scope.actorLocationsGridOptions = {
                name: 'actorLocations',
                idReferenceType: Enums.ReferenceType.IdLocation,
                type: GridTypes.PAGINATED_SMALL,
                buttonsOptions: {
                    enabled: true,
                },
                addCustomFilters: true,
                dataSource: locations,
                columnsDictionary: columns,
                allowColumnReordering: !this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActorLocations)
            }
        }, 100)
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ACTOR_DEL) ? Activity[Activity.ACTOR_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }


    private setTemplateConsts(): void {
        this.$scope.ACTOR_COMMON_TAB_NAME = this.ACTOR_COMMON_TAB_NAME
        this.$scope.ACTOR_GROUPS_ACCORDION_NAME = this.ACTOR_GROUPS_ACCORDION_NAME
        this.$scope.ACTOR_LOCATIONS_ACCORDION_NAME = this.ACTOR_LOCATIONS_ACCORDION_NAME
        this.$scope.ACTOR_PARAMS_ACCORDION_NAME = this.ACTOR_PARAMS_ACCORDION_NAME
    }

    // #endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}